export interface OdooClientConfig {
  baseUrl: string;
  db: string;
}

export interface OdooSession {
  uid: number;
  session_id: string;
}

export class OdooClient {
  private baseUrl: string;
  private db: string;
  uid: number | null = null;
  private sessionId: string | null = null;

  constructor({ baseUrl, db }: OdooClientConfig) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.db = db;
  }

  async login(login: string, password: string): Promise<OdooSession> {
    const res = await this._rpc('/web/session/authenticate', { db: this.db, login, password }) as OdooSession;
    if (!res || !res.uid) throw new Error('Authentication failed');
    this.uid = res.uid;
    this.sessionId = res.session_id;
    return res;
  }

  async call(model: string, method: string, args: unknown[] = [], kwargs: Record<string, unknown> = {}): Promise<unknown> {
    return this._rpc('/web/dataset/call_kw', { model, method, args, kwargs });
  }

  searchRead(model: string, domain: unknown[] = [], fields: string[] = [], opts: Record<string, unknown> = {}) {
    return this.call(model, 'search_read', [domain, fields], opts) as Promise<Record<string, unknown>[]>;
  }

  read(model: string, ids: number[], fields: string[] = []) {
    return this.call(model, 'read', [ids, fields]) as Promise<Record<string, unknown>[]>;
  }

  create(model: string, values: Record<string, unknown>) {
    return this.call(model, 'create', [values]) as Promise<number>;
  }

  write(model: string, ids: number[], values: Record<string, unknown>) {
    return this.call(model, 'write', [ids, values]) as Promise<boolean>;
  }

  unlink(model: string, ids: number[]) {
    return this.call(model, 'unlink', [ids]) as Promise<boolean>;
  }

  private async _rpc(path: string, params: unknown): Promise<unknown> {
    const r = await fetch(this.baseUrl + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params, id: Math.floor(Math.random() * 1e9) }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json() as { error?: { data?: { message?: string }; message?: string }; result: unknown };
    if (data.error) throw new Error(data.error.data?.message ?? data.error.message ?? 'RPC error');
    return data.result;
  }
}

export const MDD_TO_ODOO: Record<string, { model: string; link: string | null }> = {
  project:           { model: 'trace.cctp.project',          link: null },
  cadrage:           { model: 'trace.cctp.lot.cadrage',       link: 'lot_cadrage_id' },
  site_donnees:      { model: 'trace.cctp.lot.site',          link: 'lot_site_donnees_id' },
  etudes:            { model: 'trace.cctp.lot.etudes',        link: 'lot_etudes_id' },
  production_pv:     { model: 'trace.cctp.lot.production_pv', link: 'lot_production_pv_id' },
  structure:         { model: 'trace.cctp.lot.structure',     link: 'lot_structure_id' },
  conversion_elec:   { model: 'trace.cctp.lot.conversion',    link: 'lot_conversion_elec_id' },
  stockage_bess:     { model: 'trace.cctp.lot.bess',          link: 'lot_stockage_bess_id' },
  ems_supervision:   { model: 'trace.cctp.lot.ems',           link: 'lot_ems_supervision_id' },
  usages_pilotes:    { model: 'trace.cctp.lot.usages',        link: 'lot_usages_pilotes_id' },
  acc_reglementaire: { model: 'trace.cctp.lot.acc',           link: 'lot_acc_reglementaire_id' },
  execution:         { model: 'trace.cctp.lot.execution',     link: 'lot_execution_id' },
  maintenance:       { model: 'trace.cctp.lot.maintenance',   link: 'lot_maintenance_id' },
};

export class CctpService {
  constructor(private client: OdooClient) {}

  listProjects() {
    return this.client.searchRead('trace.cctp.project', [], [
      'id', 'name', 'maitre_ouvrage', 'puissance_pv_kwc', 'capacite_bess_kwh', 'date_debut', 'indice',
    ]);
  }

  async loadProject(id: number) {
    const [project] = await this.client.read('trace.cctp.project', [id]);
    const lots: Record<string, Record<string, unknown>> = {};
    for (const [code, m] of Object.entries(MDD_TO_ODOO)) {
      if (!m.link) continue;
      const proj = project as Record<string, unknown>;
      const lotRef = proj[m.link] as [number] | false;
      if (lotRef) {
        const [lot] = await this.client.read(m.model, [lotRef[0]]);
        lots[code] = lot as Record<string, unknown>;
      }
    }
    return { project, lots };
  }

  async createProject(values: Record<string, unknown>) {
    const projectId = await this.client.create('trace.cctp.project', values);
    const linkValues: Record<string, number> = {};
    for (const [, m] of Object.entries(MDD_TO_ODOO)) {
      if (!m.link) continue;
      const lotId = await this.client.create(m.model, { project_id: projectId });
      linkValues[m.link] = lotId;
    }
    await this.client.write('trace.cctp.project', [projectId], linkValues);
    return projectId;
  }

  async saveLot(projectId: number, lotCode: string, values: Record<string, unknown>) {
    const m = MDD_TO_ODOO[lotCode];
    if (!m?.link) throw new Error(`Cannot save lot ${lotCode}`);
    const [proj] = await this.client.read('trace.cctp.project', [projectId], [m.link]);
    const lotRef = (proj as Record<string, unknown>)[m.link] as [number] | false;
    if (!lotRef) throw new Error(`Lot ${lotCode} not provisioned`);
    return this.client.write(m.model, [lotRef[0]], values);
  }
}
